import { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useWorkspace } from '../hooks/useWorkspace.js'
import { useSnapshots } from '../hooks/useSnapshots.js'
import { useWebSocket } from '../hooks/useWebSocket.js'
import { listTelemetry } from '../api/vestige.js'
import StatusBadge from '../components/StatusBadge.jsx'
import SnapshotTimeline from '../components/SnapshotTimeline.jsx'
import TelemetryStream from '../components/TelemetryStream.jsx'
import TelemetryChart from '../components/TelemetryChart.jsx'
import EvalPanel from '../components/EvalPanel.jsx'
import AgentOverridePanel from '../components/AgentOverridePanel.jsx'

const MAX_TELEMETRY_EVENTS = 200

export default function WorkspaceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()

  const { workspace, loading: wsLoading, error: wsError, update: updateWorkspace } = useWorkspace(id)
  const { snapshots, loading: snapLoading, error: snapError, append: appendSnapshot, rollback, rollingBack } = useSnapshots(id)

  const [telemetry, setTelemetry] = useState([])
  const [telemetryLoading, setTelemetryLoading] = useState(true)
  const [selectedSnapshot, setSelectedSnapshot] = useState(null)

  // Load initial telemetry
  useEffect(() => {
    if (!id) return
    setTelemetryLoading(true)
    listTelemetry(id, 100)
      .then((data) => setTelemetry(data))
      .catch(() => {})
      .finally(() => setTelemetryLoading(false))
  }, [id])

  const appendTelemetry = useCallback((event) => {
    setTelemetry((prev) => {
      const next = [...prev, event]
      return next.length > MAX_TELEMETRY_EVENTS ? next.slice(-MAX_TELEMETRY_EVENTS) : next
    })
  }, [])

  const pollFallback = useCallback(() => {
    listTelemetry(id, 20)
      .then((fresh) => {
        setTelemetry((prev) => {
          const existingIds = new Set(prev.map((e) => e.id))
          const newEvents = fresh.filter((e) => !existingIds.has(e.id))
          if (!newEvents.length) return prev
          const combined = [...prev, ...newEvents]
          return combined.length > MAX_TELEMETRY_EVENTS ? combined.slice(-MAX_TELEMETRY_EVENTS) : combined
        })
      })
      .catch(() => {})
  }, [id])

  const handleWsSnapshot = useCallback((data) => appendSnapshot(data), [appendSnapshot])
  const handleWsTelemetry = useCallback((data) => appendTelemetry(data), [appendTelemetry])

  const { connected, usingFallback } = useWebSocket(id, {
    onSnapshot: handleWsSnapshot,
    onTelemetry: handleWsTelemetry,
    fallbackFn: pollFallback,
    pollInterval: 5000,
  })

  const handleWorkspaceUpdate = useCallback((updated) => {
    updateWorkspace(() => updated)
  }, [updateWorkspace])

  const handleRollback = useCallback(async (snapshotId) => {
    await rollback(snapshotId)
  }, [rollback])

  if (wsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="text-xs font-mono text-text-faint">Loading workspace…</span>
      </div>
    )
  }

  if (wsError || !workspace) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="rounded-lg border border-red bg-red-faint px-6 py-4">
          <p className="text-sm text-red mb-1">Workspace not found</p>
          <p className="text-xs text-text-dim">{wsError}</p>
        </div>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-xs text-accent hover:underline"
        >
          Back to workspaces
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-6">
      {/* Breadcrumb + header */}
      <div>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 text-xs text-text-dim hover:text-accent transition-colors mb-4 group"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M8 2L4 6l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          All workspaces
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-xl font-semibold text-text tracking-tight">
                {workspace.name}
              </h1>
              <StatusBadge status={workspace.status} />
            </div>
            {workspace.description && (
              <p className="text-xs text-text-dim leading-relaxed max-w-xl">
                {workspace.description}
              </p>
            )}
            <div className="flex items-center gap-4 text-2xs font-mono text-text-faint">
              {workspace.agentId && <span>agent: {workspace.agentId}</span>}
              <span>path: {workspace.basePath}</span>
              {connected && (
                <span className="text-green flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green animate-pulse" aria-hidden="true" />
                  live
                </span>
              )}
              {usingFallback && !connected && (
                <span className="text-yellow flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-yellow" aria-hidden="true" />
                  polling every 5s
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main layout: timeline left, panels right */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
        {/* Left: snapshot timeline */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xs font-mono text-text-faint tracking-widest uppercase">
              Snapshot timeline
            </h2>
            <span className="text-xs font-mono text-accent">
              {snapshots.length} snapshot{snapshots.length !== 1 ? 's' : ''}
            </span>
          </div>
          <SnapshotTimeline
            snapshots={snapshots}
            loading={snapLoading}
            error={snapError}
            onRollback={handleRollback}
            rollingBack={rollingBack}
            onSelectSnapshot={setSelectedSnapshot}
          />
        </div>

        {/* Right: panels stack */}
        <div className="flex flex-col gap-4 lg:sticky lg:top-[4.5rem]">
          <AgentOverridePanel
            workspace={workspace}
            onWorkspaceUpdate={handleWorkspaceUpdate}
          />
          <EvalPanel snapshot={selectedSnapshot} />
        </div>
      </div>

      {/* Bottom: telemetry stream + chart */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <div style={{ height: '360px' }}>
          <TelemetryStream
            events={telemetry}
            loading={telemetryLoading}
            connected={connected}
            usingFallback={usingFallback}
          />
        </div>
        <div>
          <TelemetryChart events={telemetry} />
        </div>
      </div>
    </div>
  )
}
